import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiExternalLink, FiShield, FiInbox, FiCalendar } from 'react-icons/fi';
import { authAPI, delegationsAPI } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';

/**
 * "Bana Verilen Vekaletler" — the delegations other people granted to me that
 * are usable right now. Picking one opens that person's workspace in a new tab
 * (see DelegationHandoff), the same shape as the owner's "Log in as".
 */
function ReceivedDelegationsModal({ onClose, delegations: initial }) {
  const { t } = useTranslation();
  const [delegations, setDelegations] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const [enteringId, setEnteringId] = useState(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Always re-fetch on open: the list the header cached may be a minute old,
  // and a delegation revoked in the meantime should not be offered.
  useEffect(() => {
    let cancelled = false;
    delegationsAPI.received()
      .then((res) => {
        if (cancelled) return;
        setDelegations(res.data?.delegations || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || t('delegation.loadFailed'));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [t]);

  const handleEnter = async (delegation) => {
    setError(null);
    // Open the tab inside the click handler so the browser counts it as
    // user-initiated; it is redirected once the handoff token arrives.
    const newTab = window.open('about:blank', '_blank');
    if (!newTab) {
      setError(t('delegation.popupBlocked'));
      return;
    }
    setEnteringId(delegation._id);
    try {
      const res = await authAPI.enterDelegation(delegation._id);
      newTab.location.href = `/delegation/handoff?token=${encodeURIComponent(res.data.handoffToken)}`;
      // Sever the opener link (tabnabbing) now that the redirect is done.
      try { newTab.opener = null; } catch { /* ignore — cross-origin, benign */ }
      onClose();
    } catch (err) {
      try { newTab.close(); } catch { /* ignore */ }
      setError(err.response?.data?.error || t('delegation.enterFailed'));
      setEnteringId(null);
    }
  };

  const scopeLabels = (d) => [
    d.scopes?.canInitiateContract && t('delegation.scopes.initiate'),
    d.scopes?.canAdvanceProcess && t('delegation.scopes.advance'),
  ].filter(Boolean);

  return (
    <div
      className="impersonate-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="received-delegations-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="impersonate-modal__panel">
        <div className="impersonate-modal__head">
          <div className="impersonate-modal__head-text">
            <div id="received-delegations-title" className="impersonate-modal__title">
              {t('delegation.receivedTitle')}
            </div>
            <div className="impersonate-modal__subtitle">
              {t('delegation.receivedSubtitle')}
            </div>
          </div>
          <button
            type="button"
            className="impersonate-modal__close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <FiX size={18} />
          </button>
        </div>

        {error && <div className="impersonate-modal__error">{error}</div>}

        <div className="impersonate-modal__list">
          {loading ? (
            <div className="impersonate-modal__empty">{t('common.loading')}</div>
          ) : delegations.length === 0 ? (
            <div className="impersonate-modal__empty">
              <FiInbox size={18} style={{ marginBottom: 6, opacity: 0.5 }} />
              <div>{t('delegation.receivedEmpty')}</div>
            </div>
          ) : (
            delegations.map((d) => {
              const owner = d.delegatorId || {};
              return (
                <button
                  key={d._id}
                  type="button"
                  className="impersonate-modal__item"
                  onClick={() => handleEnter(d)}
                  disabled={enteringId === d._id}
                >
                  <div className="impersonate-modal__item-avatar">
                    {owner.firstName?.[0]}{owner.lastName?.[0]}
                  </div>
                  <div className="impersonate-modal__item-info">
                    <div className="impersonate-modal__item-name">
                      {owner.firstName} {owner.lastName}
                    </div>
                    <div className="impersonate-modal__item-email">
                      {scopeLabels(d).join(' · ')}
                    </div>
                    <div className="impersonate-modal__item-email">
                      <FiCalendar style={{ verticalAlign: '-2px', marginRight: 4 }} />
                      {t('delegation.validUntil', { date: formatDate(d.endDate) })}
                    </div>
                  </div>
                  <FiExternalLink />
                </button>
              );
            })
          )}
        </div>

        <div className="impersonate-modal__foot">
          <FiShield />
          <span>{t('delegation.auditNote')}</span>
          <FiExternalLink style={{ marginLeft: 'auto' }} />
        </div>
      </div>
    </div>
  );
}

export default ReceivedDelegationsModal;
